import produce from "immer";
import { get as getPathValue, set as setPathValue } from "lodash";
import type { StateCreator } from "zustand";
import {
  getBlockPathInfo,
  getParentArray,
} from "@circle-react-shared/BuilderUI/Helpers/blockPath";
import { isChildrenProp } from "@circle-react-shared/BuilderUI/Helpers/typePredicates";
import type { BaseBlock, ChildrenProp } from "../DataModel.types";
import type { Middlewares, Mutators } from "./index";
import type { BlockManagementState, BuilderUIState } from "./types";
import {
  batchUpdates,
  canMoveBlockInDirection,
  duplicateBlockAtPath,
  enrichBlockWithMeta,
  findBlockById,
  findBlockPathById,
  getRelativePath,
  insertClonedBlocks,
  resolveInsertIndex,
} from "./utils/blockUpdateUtils";
import getCanDeleteSelectedBlock from "./utils/getCanDeleteSelectedBlock";
import getCanDuplicateSelectedBlock from "./utils/getCanDuplicateSelectedBlock";
import getCanMoveSelectedBlock from "./utils/getCanMoveSelectedBlock";
import getSelectedBlock from "./utils/getSelectedBlock";

export const createBlockManagementSlice: StateCreator<
  BuilderUIState,
  Middlewares,
  Mutators,
  BlockManagementState
> = (set, get) => ({
  selectedBlockPath: null,

  sortBlocks: (blocks: BaseBlock[]) => {
    const { updateBlocksFromCurrentPage, getBlocks } = get();
    const currentBlocks = getBlocks();

    const newBlocks = produce(currentBlocks, draft => {
      if (draft?.props?.children?.value) {
        draft.props.children.value = blocks;
      }
    });
    updateBlocksFromCurrentPage(newBlocks);
  },

  setSelectedBlockPath: path =>
    set(
      { selectedBlockPath: path },
      undefined,
      "blockManagement/setSelectedBlockPath",
    ),

  getSelectedBlock: () => {
    const { selectedBlockPath, getBlocks, getBlockMetaMapping } = get();

    return getSelectedBlock({
      selectedBlockPath,
      getBlocks,
      getBlockMetaMapping,
    });
  },

  getBlock: blockId => {
    const { getBlocks, getBlockMetaMapping } = get();
    const block = findBlockById(getBlocks(), blockId);
    return enrichBlockWithMeta(block, getBlockMetaMapping());
  },

  addBlocks: (block, path, index, scrollToElement = false) => {
    const { updateBlocksFromCurrentPage, getBlocks, listPolicies } = get();
    const blocks = getBlocks();
    const insertIndex: number = resolveInsertIndex({
      blocks,
      path,
      block,
      index,
      listPolicies,
    });

    const newBlocks = produce(blocks, draft => {
      const maybeChildrenProp: unknown = getPathValue(draft, path);
      if (!isChildrenProp(maybeChildrenProp)) return;
      const childrenProp: ChildrenProp = maybeChildrenProp;
      insertClonedBlocks({ childrenProp, insertIndex, block });
    });

    const newBlockPath = `blocks.${path}.value.${insertIndex}`;

    batchUpdates([
      () => updateBlocksFromCurrentPage(newBlocks),
      () =>
        set(
          {
            selectedBlockPath: newBlockPath,
            ...(scrollToElement && { blockPathToBeScrolledTo: newBlockPath }),
          },
          false,
          "blockManagement/addBlocks",
        ),
    ]);
  },

  addBlocksToRoot: (
    block,
    rootPath = "props.children",
    scrollToBlock = false,
  ) => {
    get().addBlocks(block, rootPath, undefined, scrollToBlock);
  },

  updateSelectedBlock: updatedBlock => {
    const { selectedBlockPath, getBlocks, updateBlocksFromCurrentPage } = get();
    if (!selectedBlockPath) return;

    const relativePath = getRelativePath(selectedBlockPath);
    const newBlocks = produce(getBlocks(), draft => {
      setPathValue(draft, relativePath, updatedBlock);
    });

    updateBlocksFromCurrentPage(newBlocks);
  },

  updateBlockText: (blockPath, newText) => {
    const { getBlocks, updateBlocksFromCurrentPage } = get();
    const relativePath = getRelativePath(blockPath);
    const newBlocks = produce(getBlocks(), draft => {
      const target = getPathValue(draft, relativePath) as BaseBlock;

      if (target?.props?.text) {
        target.props.text.value = newText;
      }
    });
    updateBlocksFromCurrentPage(newBlocks);
  },

  removeSelectedBlock: () => {
    const {
      selectedBlockPath,
      getBlocks,
      updateBlocksFromCurrentPage,
      listPolicies,
    } = get();
    if (!selectedBlockPath) return;
    const { currentIndex, relativeParentPath } =
      getBlockPathInfo(selectedBlockPath);
    if (currentIndex === -1) return;

    const newBlocks = produce(getBlocks(), draft => {
      const draftParentArray = getParentArray(draft, relativeParentPath);
      if (!draftParentArray) return;

      const selectedBlock = draftParentArray[currentIndex];
      if (!selectedBlock) return;

      const policy = listPolicies?.[relativeParentPath];
      if (policy?.isDeletable && !policy.isDeletable(selectedBlock)) return;

      draftParentArray.splice(currentIndex, 1);
    });
    batchUpdates([
      () =>
        set(
          { selectedBlockPath: null },
          undefined,
          "blockManagement/removeSelectedBlock",
        ),
      () => updateBlocksFromCurrentPage(newBlocks),
    ]);
  },

  getCanDeleteSelectedBlock: () => {
    const { selectedBlockPath, getBlocks, listPolicies } = get();
    return getCanDeleteSelectedBlock({
      selectedBlockPath,
      getBlocks,
      listPolicies,
    });
  },

  getCanDuplicateSelectedBlock: () => {
    const { selectedBlockPath, getBlocks, listPolicies } = get();
    return getCanDuplicateSelectedBlock({
      selectedBlockPath,
      getBlocks,
      listPolicies,
    });
  },

  getCanMoveSelectedBlock: direction => {
    const { selectedBlockPath, getBlocks, listPolicies } = get();
    return getCanMoveSelectedBlock({
      selectedBlockPath,
      getBlocks,
      listPolicies,
      direction,
    });
  },

  getCanMoveBlock: (blockId, direction) => {
    const { getBlocks, listPolicies } = get();
    const blocks = getBlocks();

    const blockPath = findBlockPathById(blocks, blockId);
    if (!blockPath) return false;
    return canMoveBlockInDirection(blockPath, direction, blocks, listPolicies);
  },

  moveSelectedBlock: direction => {
    const {
      selectedBlockPath,
      getBlocks,
      getCanMoveSelectedBlock,
      updateBlocksFromCurrentPage,
    } = get();
    if (!selectedBlockPath || !getCanMoveSelectedBlock(direction)) return;

    const { currentIndex, relativeParentPath } =
      getBlockPathInfo(selectedBlockPath);
    if (currentIndex === -1) return;

    const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;

    const newBlocks = produce(getBlocks(), draft => {
      const parentArray = getParentArray(draft, relativeParentPath);
      if (!parentArray) return;

      [parentArray[currentIndex], parentArray[newIndex]] = [
        parentArray[newIndex],
        parentArray[currentIndex],
      ];
    });

    const newSelectedBlockPath = selectedBlockPath.replace(
      /\d+$/,
      newIndex.toString(),
    );

    batchUpdates([
      () => updateBlocksFromCurrentPage(newBlocks),
      () =>
        set(
          { selectedBlockPath: newSelectedBlockPath },
          undefined,
          "blockManagement/moveSelectedBlock",
        ),
    ]);
  },

  removeBlockSelection: () =>
    set(
      { selectedBlockPath: null },
      undefined,
      "blockManagement/removeBlockSelection",
    ),

  duplicateSelectedBlock: () => {
    const { selectedBlockPath, getBlocks, updateBlocksFromCurrentPage } = get();
    if (!selectedBlockPath) return;

    const result = duplicateBlockAtPath(getBlocks(), selectedBlockPath);
    if (!result) return;

    batchUpdates([
      () => updateBlocksFromCurrentPage(result.newBlocks),
      () =>
        set(
          { selectedBlockPath: result.newBlockPath },
          undefined,
          "blockManagement/duplicateSelectedBlock",
        ),
    ]);
  },

  renameBlock: (newName: string, blockId: string) => {
    const { getBlocks, updateBlocksFromCurrentPage } = get();
    const blocks = getBlocks();
    const blockPath = findBlockPathById(blocks, blockId);
    if (!blockPath) return;

    const relativePath = getRelativePath(blockPath);

    const newBlocks = produce(blocks, draft => {
      const block = getPathValue(draft, relativePath) as BaseBlock;
      if (!block) return;
      if (!block.meta) {
        block.meta = {};
      }
      block.meta.name = newName;
    });
    updateBlocksFromCurrentPage(newBlocks);
  },
});
